import {
    BoundaryPartRequestBoundaryType,
    BoundaryPartRequestGeometryType as GeometryType
} from 'generatedTypes/flight/models';
import { useMemo } from 'react';
import { latLonsToPositions } from 'utils/coordinateConversion';

import { SYNTHETIC_SWATH_METERS } from './constants';
import type {
    CreateBoundaryRequest,
    FlightResponse,
    Geometry,
    LatLon,
    PlanResult,
    UseMapDataParams,
    UseMapDataReturn
} from './types';

// TODO(schema-mismatch): Generated Polygon.coordinates is typed as JTS Coordinate[]
// but at runtime the API delivers GeoJSON Position[][] (per useDTO.convertFromDTO).
// The cast bridges the mismatch. Resolve by either regenerating Orval against a
// spec that declares GeoJSON, or by routing through convertFromDTO explicitly.
// See also: src/hooks/useSavePlan/index.ts:89.
type RawPolygon = { coordinates: unknown };

const buildPolygon = ({ ring }: { ring: LatLon[] }): RawPolygon => {
    const exterior = latLonsToPositions({ latLons: ring });

    return { coordinates: [exterior] };
};

// Polygon geometry type — we always emit POLYGON (not CIRCLE) for planner previews.
const GEOMETRY_TYPE_POLYGON = GeometryType.POLYGON;

const buildGeometryFromLatLons = ({
    boundary,
    obstacles
}: {
    boundary: LatLon[];
    obstacles: LatLon[][];
}): Geometry['parts'] => {
    const applyPart = {
        boundary: buildPolygon({ ring: boundary }),
        boundaryType: BoundaryPartRequestBoundaryType.APPLY,
        geometryType: GEOMETRY_TYPE_POLYGON
    };

    const obstacleParts = obstacles.map(ring => ({
        boundary: buildPolygon({ ring }),
        boundaryType: BoundaryPartRequestBoundaryType.NO_ENTRY,
        geometryType: GEOMETRY_TYPE_POLYGON
    }));

    return [applyPart, ...obstacleParts] as unknown as Geometry['parts'];
};

const buildGeometry = ({
    boundary,
    draftBoundary,
    isEditingBoundary,
    obstacles
}: {
    boundary: LatLon[] | null;
    draftBoundary: CreateBoundaryRequest | null;
    isEditingBoundary: boolean;
    obstacles: LatLon[][];
}): Geometry | undefined => {
    // When a draft exists, its boundaryParts become the authoritative source of
    // truth for the map preview — the draft may contain additions or deletions
    // that haven't been saved yet.
    // TODO(schema-mismatch): CreateBoundaryRequest.boundaryParts (generated Orval type) and Geometry['parts'] (Map component internal type) have a structural mismatch the Flight Planner bridges until backend coordinates on type regeneration. Tracked as an open question in PRD §13. See also: src/hooks/useSavePlan/index.ts:89.
    const draftParts = draftBoundary?.boundaryParts as unknown as Geometry['parts'] | undefined;

    if (draftParts && draftParts.length > 0) {
        return {
            isEditable: isEditingBoundary,
            parts: draftParts
        };
    }

    if (!boundary) {
        return undefined;
    }

    return {
        isEditable: isEditingBoundary,
        parts: buildGeometryFromLatLons({ boundary, obstacles })
    };
};

// TODO(schema-mismatch): We synthesize a FlightResponse-shaped object to reuse
// `useFlightPaths`'s existing Mapbox layer pipeline (FLIGHT_PATH_LINE). The real
// engine input is PlanWaypoint[] but the Map component only supports FlightResponse
// today. Resolve by adding a first-class `flightPath` prop to <Map> that accepts a
// pre-built LineString, eliminating the need to fake a FlightResponse.
// See also: src/hooks/useSavePlan/index.ts:89 (sibling JTS/GeoJSON cast).
const buildFlightPaths = ({ planResult }: { planResult: PlanResult | null }): FlightResponse[] => {
    if (!planResult || planResult.waypoints.length === 0) {
        return [];
    }

    // Use ALL waypoints in execution order (including TURN/TRANSIT/START/END) so the
    // rendered LineString follows the drone's actual path, not diagonal shortcuts.
    const pathPoints = planResult.waypoints;

    if (pathPoints.length < 2) {
        return [];
    }

    const coordinates = pathPoints.map(point => [point.lon, point.lat]);

    // Invariant: useFlightPaths tolerates an empty sprayPath.coordinates array
    // (the internal for-loop skips iterations on empty input). If useFlightPaths
    // is ever refactored to require non-empty spray coordinates, this adapter
    // breaks. See the TODO(schema-mismatch) above for the long-term fix.
    const synthetic = {
        flightPath: { coordinates },
        sprayPath: { coordinates: [] as number[][][] },
        swathWidthMeters: SYNTHETIC_SWATH_METERS
    };

    return [synthetic as unknown as FlightResponse];
};

const useMapData = ({
    boundary,
    draftBoundary,
    isEditingBoundary,
    obstacles,
    planResult
}: UseMapDataParams): UseMapDataReturn => {
    const geometry = useMemo(
        () => buildGeometry({ boundary, draftBoundary, isEditingBoundary, obstacles }),
        [boundary, draftBoundary, isEditingBoundary, obstacles]
    );
    const flightPaths = useMemo(() => buildFlightPaths({ planResult }), [planResult]);

    return { flightPaths, geometry };
};

export default useMapData;
